<template>
    <ValidationObserver ref="observer" tag="div">
        <!-- ── Resumen de compra ── -->
        <div class="tw:flex tw:items-center tw:gap-2 tw:mb-4">
            <svg class="tw:w-5 tw:h-5 tw:text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
            </svg>
            <h3 class="tw:text-lg tw:font-bold tw:text-slate-800 tw:mb-0">Resumen de compra</h3>
        </div>

        <div class="tw:overflow-x-auto tw:rounded-xl tw:border tw:border-slate-200 tw:mb-8">
            <table class="tw:w-full tw:bg-white">
                <thead class="tw:bg-slate-50 tw:border-b tw:border-slate-200">
                    <tr>
                        <th class="tw:text-left tw:py-3 tw:px-5 tw:text-xs tw:font-semibold tw:text-slate-500 tw:uppercase tw:tracking-wide">Concepto</th>
                        <th class="tw:text-right tw:py-3 tw:px-5 tw:text-xs tw:font-semibold tw:text-slate-500 tw:uppercase tw:tracking-wide">Importe</th>
                    </tr>
                </thead>
                <tbody class="tw:divide-y tw:divide-slate-100">
                    <!-- Categoría -->
                    <tr>
                        <td class="tw:py-4 tw:px-5 tw:text-sm tw:text-slate-700">
                            Cuota categoría <span class="tw:font-semibold">{{ categoriaNombre }}</span>
                        </td>
                        <td class="tw:py-4 tw:px-5 tw:text-sm tw:text-slate-700 tw:text-right">
                            <!-- Con plazos: precio tachado + importe del primer plazo -->
                            <template v-if="tienePlazos">
                                <span class="tw:line-through tw:text-slate-400">{{ formatPrecio(precioBase) }}</span>
                                <span class="tw:ml-1.5 tw:font-semibold tw:text-blue-600">{{ formatPrecio(precioBasePrimerPlazo) }}</span>
                                <span class="tw:block tw:text-[10px] tw:leading-tight tw:text-slate-400">(1.er plazo)</span>
                            </template>

                            <!-- Sin plazos: descuento normal -->
                            <template v-else>
                                <template v-if="tieneDescuento">
                                    <span class="tw:line-through tw:text-slate-400">{{ formatPrecio(precioBase) }}</span>
                                    <span class="tw:ml-1.5">
                                        {{ descuentoAplicado > 0 ? '(-' + descuentoAplicado + '%)' : '(-' + formatPrecio(descuentoIntegro) + ')' }}
                                    </span>
                                </template>
                                <span class="tw:font-semibold" :class="{ 'tw:text-green-600': tieneDescuento }">
                                    {{ formatPrecio(precioFinal) }}
                                </span>
                            </template>
                        </td>
                    </tr>

                    <!-- Indumentarias -->
                    <template v-if="indumentariasElegidas.length">
                        <tr v-for="ind in indumentariasElegidas" :key="ind.id + '-' + ind.talla?.id">
                            <td class="tw:py-4 tw:px-5 tw:text-sm tw:text-slate-700">
                                {{ ind.nombre }}
                                <span class="tw:text-xs tw:text-slate-400">
                                    ({{ ind.talla?.nombre }} — {{ ind.unidades }} ud)
                                </span>
                            </td>
                            <td class="tw:py-4 tw:px-5 tw:text-sm tw:text-slate-700 tw:text-right">
                                <!-- Indumentaria gratuita incluida -->
                                <template v-if="ind.incluida && ind.unidades === 1">
                                    <span class="tw:text-green-600">0,00 €</span>
                                </template>

                                <!-- Con plazos + indumentaria fraccionada: precio tachado + parte proporcional -->
                                <template v-else-if="tienePlazos && indumentariaPlazos">
                                    <span class="tw:line-through tw:text-slate-400">{{ formatPrecio(ind.precio_total || ind.precio * ind.unidades) }}</span>
                                    <span class="tw:ml-1 tw:font-medium">{{ formatPrecio(getIndImportePlazo(ind)) }}</span>
                                </template>

                                <!-- Con plazos + indumentaria al contado: precio completo -->
                                <template v-else-if="tienePlazos && !indumentariaPlazos">
                                    <span class="tw:font-medium">{{ formatPrecio(ind.precio_total || ind.precio * ind.unidades) }}</span>
                                    <span class="tw:block tw:text-[10px] tw:leading-tight tw:text-slate-400">(al contado)</span>
                                </template>

                                <!-- Sin plazos: precio completo normal -->
                                <template v-else>
                                    <span class="tw:font-medium">{{ formatPrecio(ind.precio_total || ind.precio * ind.unidades) }}</span>
                                </template>
                            </td>
                        </tr>
                    </template>
                </tbody>
                <tfoot>
                    <tr class="tw:bg-red-50/50">
                        <td class="tw:py-4 tw:px-5 tw:text-base tw:font-bold tw:text-slate-800">TOTAL</td>
                        <td class="tw:py-4 tw:px-5 tw:text-base tw:font-bold tw:text-primary tw:text-right">
                            {{ formatPrecio(totalFinal) }}<span class="tw:text-xs tw:font-normal tw:text-slate-400">*</span>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
        <div class="tw:-mt-6 tw:mb-6 tw:space-y-1">
            <!-- Info: descuento no aplica a indumentarias (solo sin plazos) -->
            <p
                v-if="!tienePlazos && tieneDescuento && indumentariasElegidas.length > 0"
                class="tw:text-xs tw:text-amber-600 tw:italic tw:flex tw:items-center tw:gap-1"
            >
                <svg class="tw:w-3.5 tw:h-3.5 tw:shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                </svg>
                <span>El descuento <strong>no se aplica a las indumentarias</strong>.</span>
            </p>

            <!-- Info: tabla de plazos calculados (siempre visible) -->
            <div
                v-if="tienePlazos"
                class="tw:pt-3"
            >
                <div class="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-blue-600 tw:font-medium tw:mb-2">
                    <svg class="tw:w-3.5 tw:h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                    </svg>
                    Detalle de los {{ plazos.length }} plazos
                </div>
                <div class="tw:overflow-x-auto tw:rounded-lg tw:border tw:border-slate-200">
                    <table class="tw:text-xs tw:min-w-[480px] tw:table-fixed">
                        <thead class="tw:bg-slate-50">
                            <tr>
                                <th class="tw:py-1.5 tw:px-3 tw:text-left tw:font-medium tw:text-slate-500 tw:w-max">#</th>
                                <th class="tw:py-1.5 tw:px-3 tw:text-left tw:font-medium tw:text-slate-500 tw:w-max">Fecha</th>
                                <th class="tw:py-1.5 tw:px-3 tw:text-start tw:font-medium tw:text-slate-500 tw:w-max">Gastos gestión</th>
                                <th class="tw:py-1.5 tw:px-3 tw:text-start tw:font-medium tw:text-slate-500 tw:w-max">Importe</th>
                            </tr>
                        </thead>
                        <tbody class="tw:divide-y tw:divide-slate-100">
                            <tr v-for="(pl, i) in plazos" :key="i" :class="{ 'tw:bg-blue-50/50': i === 0 }">
                                <td class="tw:py-1.5 tw:px-3 tw:text-slate-600">{{ pl.id || i + 1 }}</td>
                                <td class="tw:py-1.5 tw:px-3 tw:text-slate-600">{{ pl.fecha }}</td>
                                <td class="tw:py-1.5 tw:px-3 tw:text-start tw:text-slate-500">{{ formatPrecio(pl.price_management) }}</td>
                                <td class="tw:py-1.5 tw:px-3 tw:text-start tw:font-medium">{{ formatPrecio(pl.importe + pl.price_management) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p class="tw:text-xs tw:text-slate-500 tw:italic tw:mt-2">
                    *El primer plazo se le cobrará a continuación, y los siguientes en sus correspondientes fechas facilitadas arriba.
                </p>
            </div>
        </div>

        <!-- ── Pago a plazos (solo si la categoría lo permite) ── -->
        <AddPlazosModal
            v-if="categoria?.activar_pago_plazos"
            :frecuencias="categoria.frecuencias || []"
            :min-plazos="categoria.min_cantidad_plazos || 1"
            :max-plazos="categoria.max_cantidad_plazos || 12"
            :gastos="categoria.price_management_online || 0"
            :tiene-gastos="!categoria.price_management_interno_taquilla"
            :disabled="plazosLoading"
            :plazos-seleccionados="plazosSeleccionados"
            :error-plazos="errorPlazos"
            :fecha-limite-plazos="fechaLimitePlazos"
            @terminado="onPlazosTerminado"
            @reset="onPlazosReset"
        />

        <!-- ── Error del backend al calcular plazos ── -->
        <div
            v-if="plazosBackendError && !plazosSeleccionados"
            class="tw:mt-3 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-xs tw:text-red-700 tw:flex tw:items-start tw:gap-2"
        >
            <svg class="tw:w-4 tw:h-4 tw:shrink-0 tw:mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/>
            </svg>
            <span>{{ plazosBackendError }}</span>
        </div>

        <!-- ── Método de pago ── -->
        <div class="tw:flex tw:items-center tw:gap-2 tw:mt-6 tw:mb-4">
            <svg class="tw:w-6 tw:h-6 tw:text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"/>
            </svg>
            <h3 class="tw:text-lg tw:font-bold tw:text-slate-800 tw:mb-0">Método de pago</h3>
        </div>

        <BaseMultiselect
            :value="metodoPagoVal"
            :options="metodosPago"
            label="Método de pago"
            name="metodo_pago"
            rules="required"
            value-key="id"
            label-key="nombre"
            :multiple="false"
            @input="onMetodoPagoChange"
        />

        <!-- ── Calculadora de cambio (solo para efectivo/metálico) ── -->
        <ChangeCalculator
            v-if="metodoPagoVal === 1"
            :amount-due="totalFinal"
            :model-value="amountPaid"
            @amountChanged="onAmountPaidUpdate"
        />

        <!-- ── Errores ── -->
        <div v-if="errores.length" class="tw:mt-4 tw:p-4 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-sm tw:text-red-700">
            <p class="tw:font-bold tw:mb-1">Ha ocurrido un error:</p>
            <ul v-if="Array.isArray(errores)" class="tw:list-disc tw:pl-4">
                <li v-for="(err, i) in errores" :key="i">{{ err }}</li>
            </ul>
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, computed, inject, onMounted, watch } from 'vue';
    import store from '@/store';
    import filters from '@/filters';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import ChangeCalculator from './ChangeCalculator.vue';
    import AddPlazosModal from './AddPlazosModal.vue';
    import { useIndumentariaPlazos } from '@/composables/common/bases/useIndumentariaPlazos';

    const formData = inject('formData');
    const step6State = inject('step6State');

    const props = defineProps({
        importeTotal: { type: Number, default: 0 },
        metodosPago: { type: Array, default: () => [] },
        categoriaNombre: { type: String, default: '' },
        precioBase: { type: Number, default: 0 },
        categoria: { type: Object, default: null },
        plazosData: { type: Object, default: null },
        errores: { type: [Array, Object], default: () => [] },
    });

    const observer = ref(null);
    const metodoPagoVal = ref(formData?.metodo_pago || null);
    // ── Importe recibido (controlado por ChangeCalculator via v-model) ──
    const amountPaid = ref(0);

    const formatPrecio = filters.formatear_precio;

    const plazosSeleccionados = ref(false);
    const errorPlazos = ref(false);
    const fechaLimitePlazos = computed(() => {
        return store.getters.getTeatro?.fecha_limite_plazos || '';
    });

    // ── Validez del importe recibido (computed directo, sin watch problemático) ──
    const amountPaidValid = computed(() => {
        return (Number(amountPaid.value) || 0) >= totalFinal.value;
    });

    // ── Validez global del paso: el método de pago debe estar seleccionado
    //    y, si es efectivo, el importe recibido debe cubrir el total ──
    const isStepValid = computed(() => {
        if (!metodoPagoVal.value) return false;
        if (metodoPagoVal.value === 1) return amountPaidValid.value;
        return true;
    });

    const indumentariasElegidas = computed(() => formData?.indumentariasElegidas || []);
    const descuentoAplicado = computed(() => formData?.descuento_aplicado || 0);
    const descuentoIntegro = computed(() => formData?.descuento_integro || 0);

    const tieneDescuento = computed(() => descuentoAplicado.value > 0 || descuentoIntegro.value > 0);

    // ── Plazos ──
    const plazos = computed(() => props.plazosData?.plazos || []);
    const indumentariaPlazos = computed(() => props.plazosData?.indumentaria_plazos || false);
    const tienePlazos = computed(() => plazos.value.length > 0);

    const totalIndumentariasFullPrice = computed(() => {
        let total = 0;
        for (const ind of indumentariasElegidas.value) {
            if (!ind.incluida || ind.unidades > 1) {
                total += parseFloat(ind.precio_total || ind.precio * ind.unidades || 0);
            }
        }
        return total;
    });

    const { getPrecioBasePrimerPlazo, getIndumentariaImportePlazo } = useIndumentariaPlazos();

    const precioBasePrimerPlazo = computed(() =>
        getPrecioBasePrimerPlazo(plazos.value, props.precioBase, indumentariasElegidas.value.length > 0)
    );

    function getIndImportePlazo(ind) {
        return getIndumentariaImportePlazo(
            ind,
            plazos.value,
            totalIndumentariasFullPrice.value,
            indumentariaPlazos.value,
        );
    }

    const precioFinal = computed(() => {
        let precio = props.precioBase;
        if (descuentoAplicado.value > 0) {
            precio = precio - (precio * descuentoAplicado.value / 100);
        } else if (descuentoIntegro.value > 0) {
            precio = precio - descuentoIntegro.value;
        }
        return Math.max(0, precio);
    });

    const totalFinal = computed(() => {
        // Con plazos: importe del primer plazo + gastos de gestión
        if (tienePlazos.value) {
            const pl = plazos.value[0];
            return pl.importe + (pl.price_management || 0);
        }

        // Sin plazos: precio con descuento + indumentarias no incluidas
        let total = precioFinal.value;
        for (const ind of indumentariasElegidas.value) {
            if (!ind.incluida || ind.unidades > 1) {
                total += parseFloat(ind.precio_total || ind.precio * ind.unidades || 0);
            }
        }
        return total;
    });

    // ── Handlers de pago a plazos ──
    const {
        calcularPlazos: plazosCalcular,
        borrarPlazos: plazosBorrar,
        plazosData: plazosResult,
        plazosLoading,
        plazosErrorMessage: plazosBackendError,
    } = inject('plazosActions');

    // ── Resetear plazosData al reingresar al paso ──
    onMounted(() => {
        if (plazosResult.value) {
            plazosResult.value = null;
        }
    });

    async function onPlazosTerminado(data) {
        await plazosCalcular({
            categoriaId: props.categoria?.id,
            frecuenciaId: data.frecuenciaSelect,
            nPlazos: data.plazoSelect,
        });
        if (plazosResult.value) {
            plazosSeleccionados.value = true;
        }
    }

    async function onPlazosReset() {
        await plazosBorrar();
        plazosSeleccionados.value = false;
    }

    async function validate() {
        const isValid = await observer.value.validate();
        return isValid && isStepValid.value;
    }

    function onAmountPaidUpdate(val) {
        amountPaid.value = Number(val) || 0;
        // Sincronización directa con el padre (redundante con el watch, como fallback)
        if (step6State && metodoPagoVal.value === 1) {
            step6State.isStepValid = (Number(amountPaid.value) || 0) >= totalFinal.value;
        }
    }

    function onMetodoPagoChange(val) {
        metodoPagoVal.value = val;
        // Sincronización directa con el padre
        if (step6State) {
            if (!val) { step6State.isStepValid = false; return; }
            if (val === 1) { step6State.isStepValid = (Number(amountPaid.value) || 0) >= totalFinal.value; return; }
            step6State.isStepValid = true;
        }
    }

    function getData() {
        return { metodo_pago: metodoPagoVal.value };
    }

    defineExpose({
        get observer() { return observer.value; },
        validate,
        getData,
        get isStepValid() { return isStepValid.value; },
    });

    // ── Actualiza el estado compartido con Index.vue para habilitar/deshabilitar FINALIZAR ──
    //    Usamos watch + getter para evitar posibles bugs de computed anidados en Vue 2.7
    watch(
        () => {
            if (!metodoPagoVal.value) return false;
            if (metodoPagoVal.value === 1) return (Number(amountPaid.value) || 0) >= totalFinal.value;
            return true;
        },
        val => { step6State.isStepValid = val; },
        { immediate: true }
    );
</script>
